/**
 * Amorlia Persistent Storage
 * localStorage + IndexedDB utilities with debounced saving.
 * Only clears data when the user explicitly requests it.
 */

const STORAGE_PREFIX = 'amorlia_';

export const KEYS = {
  extractQueue:        `${STORAGE_PREFIX}extract_queue`,
  slData:              `${STORAGE_PREFIX}sl_data`,
  catalog:             `${STORAGE_PREFIX}catalog`,
  settings:            `${STORAGE_PREFIX}settings`,
  pricing:             `${STORAGE_PREFIX}pricing`,
  smartlayerSettings:  `${STORAGE_PREFIX}smartlayer_settings`,
  scrapeHistory:       `${STORAGE_PREFIX}scrape_history`,
  recentPrices:        `${STORAGE_PREFIX}recent_prices`,
  lastActivePage:      `${STORAGE_PREFIX}last_active_page`,
  importSessions:      `${STORAGE_PREFIX}import_sessions`,
  pageDraftStates:     `${STORAGE_PREFIX}page_draft_states`,
  pendingProducts:     `${STORAGE_PREFIX}pending_products`,
  scrapeCache:         `${STORAGE_PREFIX}scrape_cache`,
  stockHistory:        `${STORAGE_PREFIX}stock_history`,
  catalogTags:         `${STORAGE_PREFIX}catalog_tags`,
  orders:              `${STORAGE_PREFIX}orders`,
  importedOrders:      `${STORAGE_PREFIX}imported_orders`,
  importMappings:      `${STORAGE_PREFIX}import_mappings`,
  importHistory:       `${STORAGE_PREFIX}import_history`,
  auth:                `${STORAGE_PREFIX}auth`,
  activeJobs:          `${STORAGE_PREFIX}active_jobs`,
};

// ── localStorage helpers ──

export function load(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function remove(key) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

export function clearOnly(keys) {
  for (const k of keys) {
    remove(k);
  }
}

// ── Debounced save ──

const debounceTimers = {};

export function debouncedSave(key, value, delayMs = 300) {
  if (debounceTimers[key]) clearTimeout(debounceTimers[key]);
  debounceTimers[key] = setTimeout(() => {
    save(key, value);
    delete debounceTimers[key];
  }, delayMs);
}

// ── IndexedDB for large product records ──

const IDB_NAME = 'amorlia_dashboard';
const IDB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('products')) {
        db.createObjectStore('products', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbSave(storeName, record) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.put(record);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function idbSaveBatch(storeName, records) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    for (const record of records) store.put(record);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function idbLoad(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(id);
    req.onsuccess = () => { db.close(); resolve(req.result || null); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function idbLoadAll(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = () => { db.close(); resolve(req.result || []); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function idbDelete(storeName, id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.delete(id);
    req.onsuccess = () => { db.close(); resolve(); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

export async function idbClear(storeName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.clear();
    req.onsuccess = () => { db.close(); resolve(); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}
